import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu, X, Home, MapPin, BarChart3, Users, MessageSquare, Bot, Calculator,
  GraduationCap, Star, BookOpen, GitCompare, ShieldCheck, FileText,
  Globe, ChevronDown, Image, Wrench, UsersRound, Calendar
} from 'lucide-react';
import { Button } from './ui/button';

const ADMIN_EMAIL = 'bulleysamuel00@gmail.com';

const COUNTRY_META = {
  italy: { name: 'Italy', flag: '\ud83c\uddee\ud83c\uddf9' },
  spain: { name: 'Spain', flag: '\ud83c\uddea\ud83c\uddf8' },
  portugal: { name: 'Portugal', flag: '\ud83c\uddf5\ud83c\uddf9' },
};

const DropdownMenu = ({ label, icon: Icon, items, isActive, location }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const anyActive = items.some(i => location.pathname === i.to);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm transition-colors ${anyActive ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted hover:text-primary'}`}
        data-testid={`nav-dropdown-${label.toLowerCase()}`}
      >
        <Icon size={14} />
        <span>{label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-xl shadow-xl border border-border py-1 z-50">
            {items.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${location.pathname === link.to ? 'font-semibold text-primary bg-muted/50' : 'text-foreground hover:bg-muted'}`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <link.icon size={14} className="text-muted-foreground" />
                <span>{link.label}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Navbar = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const selectedCountry = localStorage.getItem('selectedCountry') || 'italy';
  const currentCountry = COUNTRY_META[selectedCountry] || COUNTRY_META.italy;

  const isAdmin = user?.email === ADMIN_EMAIL;

  const switchCountry = (id) => {
    localStorage.setItem('selectedCountry', id);
    setCountryMenuOpen(false);
    setIsOpen(false);
    if (location.pathname.startsWith('/region/')) {
      navigate('/regions');
    } else {
      window.location.reload();
    }
  };

  const primaryLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    { to: '/regions', label: 'Regions', icon: MapPin },
    { to: '/chat', label: 'AI Chat', icon: Bot },
    { to: '/visa-solutions', label: 'Visa Partners', icon: FileText },
  ];

  const toolsLinks = [
    { to: '/cost-calculator', label: 'Cost Calculator', icon: Calculator },
    { to: '/cost-compare', label: 'Cost Compare', icon: BarChart3 },
    { to: '/compare', label: 'Region Compare', icon: GitCompare },
    { to: '/country-compare', label: 'Country Compare', icon: Globe },
    { to: '/school-finder', label: 'School Finder', icon: GraduationCap },
    { to: '/relocation-planner', label: 'Relocation Planner', icon: MapPin },
  ];

  const guidesLinks = [
    { to: '/visa-guide', label: 'Visa Guide', icon: ShieldCheck },
    { to: '/language', label: 'Language', icon: BookOpen },
    { to: '/family', label: 'Family & Schools', icon: GraduationCap },
    { to: '/events', label: 'Events', icon: Calendar },
  ];

  const communityLinks = [
    { to: '/chatrooms', label: 'Chat Rooms', icon: MessageSquare },
    { to: '/forum', label: 'Forum', icon: MessageSquare },
    { to: '/stories', label: 'Expat Stories', icon: Star },
  ];

  const allMobileLinks = [...primaryLinks, ...toolsLinks, ...guidesLinks, ...communityLinks];

  const adminLinks = [
    { to: '/admin', label: 'Analytics', icon: BarChart3 },
    { to: '/admin/users', label: 'Users DB', icon: Users },
    { to: '/posters', label: 'Posters', icon: Image },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-border/40 shadow-sm" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2" data-testid="navbar-logo">
            <span className="text-xl font-bold tracking-tight text-primary">MoveToEurope</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {user && (
              <>
                {/* Country Switcher */}
                <div className="relative mr-2">
                  <button
                    onClick={() => setCountryMenuOpen(!countryMenuOpen)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-sm font-medium text-primary transition-colors"
                    data-testid="country-switcher"
                  >
                    <span>{currentCountry.flag}</span>
                    <span>{currentCountry.name}</span>
                    <ChevronDown size={14} className={`transition-transform ${countryMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {countryMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setCountryMenuOpen(false)} />
                      <div className="absolute top-full left-0 mt-1 w-44 bg-white rounded-xl shadow-xl border border-border py-1 z-50" data-testid="country-dropdown">
                        {Object.entries(COUNTRY_META).map(([id, meta]) => (
                          <button
                            key={id}
                            onClick={() => switchCountry(id)}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted transition-colors ${selectedCountry === id ? 'font-semibold text-primary bg-muted/50' : 'text-foreground'}`}
                            data-testid={`country-option-${id}`}
                          >
                            <span>{meta.flag}</span>
                            <span>{meta.name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {primaryLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm transition-colors ${isActive(link.to) ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted hover:text-primary'}`}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon size={14} />
                    <span>{link.label}</span>
                  </Link>
                ))}

                <DropdownMenu label="Tools" icon={Wrench} items={toolsLinks} location={location} />
                <DropdownMenu label="Guides" icon={BookOpen} items={guidesLinks} location={location} />
                <DropdownMenu label="Community" icon={UsersRound} items={communityLinks} location={location} />

                {isAdmin && adminLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm transition-colors ${isActive(link.to) ? 'bg-primary text-white' : 'text-amber-600 hover:bg-amber-50 hover:text-amber-700'}`}
                    data-testid={`nav-admin-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon size={14} />
                    <span>{link.label}</span>
                  </Link>
                ))}
              </>
            )}
          </div>

          {/* Auth + Mobile Toggle */}
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="rounded-full hidden lg:inline-flex"
                data-testid="logout-button"
              >
                Logout
              </Button>
            ) : (
              <div className="hidden lg:flex items-center gap-2">
                <Button onClick={() => navigate('/login')} variant="ghost" size="sm" className="rounded-full" data-testid="login-nav-button">
                  Sign In
                </Button>
                <Button onClick={() => navigate('/register')} size="sm" className="rounded-full bg-primary text-white hover:bg-primary/90" data-testid="register-nav-button">
                  Sign Up
                </Button>
              </div>
            )}

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-primary"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-border/40 shadow-xl max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {user && (
              <>
                {/* Mobile Country Switcher */}
                <div className="flex items-center gap-2 py-2 px-3 mb-2">
                  <Globe size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Country:</span>
                  <div className="flex gap-2 ml-auto">
                    {Object.entries(COUNTRY_META).map(([id, meta]) => (
                      <button
                        key={id}
                        onClick={() => switchCountry(id)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedCountry === id ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}
                        data-testid={`mobile-country-${id}`}
                      >
                        {meta.flag} {meta.name}
                      </button>
                    ))}
                  </div>
                </div>

                {primaryLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.to) ? 'bg-primary text-white' : 'text-foreground hover:bg-muted'}`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon size={16} />
                    <span>{link.label}</span>
                  </Link>
                ))}

                <div className="my-2 border-t border-border" />
                <p className="px-3 py-1 text-xs text-muted-foreground font-semibold uppercase tracking-wider">Tools</p>
                {toolsLinks.map(link => (
                  <Link key={link.to} to={link.to} onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.to) ? 'bg-primary text-white' : 'text-foreground hover:bg-muted'}`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon size={16} /><span>{link.label}</span>
                  </Link>
                ))}

                <div className="my-2 border-t border-border" />
                <p className="px-3 py-1 text-xs text-muted-foreground font-semibold uppercase tracking-wider">Guides</p>
                {guidesLinks.map(link => (
                  <Link key={link.to} to={link.to} onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.to) ? 'bg-primary text-white' : 'text-foreground hover:bg-muted'}`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon size={16} /><span>{link.label}</span>
                  </Link>
                ))}

                <div className="my-2 border-t border-border" />
                <p className="px-3 py-1 text-xs text-muted-foreground font-semibold uppercase tracking-wider">Community</p>
                {communityLinks.map(link => (
                  <Link key={link.to} to={link.to} onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.to) ? 'bg-primary text-white' : 'text-foreground hover:bg-muted'}`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon size={16} /><span>{link.label}</span>
                  </Link>
                ))}

                {isAdmin && (
                  <>
                    <div className="my-2 border-t border-border" />
                    <p className="px-3 py-1 text-xs text-amber-600 font-semibold uppercase tracking-wider">Admin</p>
                    {adminLinks.map(link => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.to) ? 'bg-primary text-white' : 'text-amber-600 hover:bg-amber-50'}`}
                        data-testid={`mobile-admin-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <link.icon size={16} />
                        <span>{link.label}</span>
                      </Link>
                    ))}
                  </>
                )}

                <div className="my-2 border-t border-border" />
                <button
                  onClick={() => { onLogout(); setIsOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="mobile-logout-button"
                >
                  Logout
                </button>
              </>
            )}
            {!user && (
              <div className="flex gap-2 py-2">
                <Button onClick={() => { navigate('/login'); setIsOpen(false); }} variant="outline" className="flex-1 rounded-full" data-testid="mobile-login-btn">
                  Sign In
                </Button>
                <Button onClick={() => { navigate('/register'); setIsOpen(false); }} className="flex-1 rounded-full bg-primary text-white" data-testid="mobile-register-btn">
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
